/**
 * Ohio EMIS, fiscal year 2026 (school year 2025–2026) — historical.
 *
 * Kept so FY26 submissions are always read, compared and reproduced with
 * FY26's own rules. It differs from FY27 where the state changed: no Staff
 * Vacancy (CR) record, diabetes-related reporting still required, and RIMP
 * reported through grade 3. Same demonstration-configuration caveat as FY27.
 */

import type { FiscalYearPackage, RecordDefinition } from '../types.js'
import { OHIO_FY27 } from './fy27.js'

const shiftYear = (d: string | null | undefined) => (d ? `${Number(d.slice(0, 4)) - 1}${d.slice(4)}` : d ?? null)

const records: RecordDefinition[] = OHIO_FY27.records
  .filter((r) => r.introducedIn !== 'FY27')
  .map((r) => (r.code === 'FD' ? { ...r, elements: r.elements.map((e) => (e.code === 'DIAB' ? { ...e, name: 'Diabetes care plan', retired: false, note: undefined } : e)) } : r))

export const OHIO_FY26: FiscalYearPackage = {
  ...OHIO_FY27,
  code: 'FY26',
  label: 'FY26 (2025–2026)',
  schoolYear: '2025-2026',
  effectiveStart: '2025-07-01',
  effectiveEnd: '2026-06-30',
  schemaVersion: 'demo-26.3',
  sourceNote: 'Riverview demonstration configuration of the FY26 EMIS record structure (historical). Used to read and reproduce FY26 submissions; never applied to FY27.',
  status: 'historical',
  records,
  rules: [
    ...OHIO_FY27.rules.filter((r) => r.recordCode !== 'CR' && r.id !== 'GQ-RIMP-GRADE').map((r) => ({ ...r, version: r.version.replace(/^27\./, '26.') })),
    { id: 'GQ-RIMP-GRADE', recordCode: 'GQ', elements: ['PROG'], severity: 'warning', description: 'RIMP is reported for grades KG through 3 in FY26', logic: { kind: 'custom', check: 'rimp_grade_band' }, version: '26.2' },
    { id: 'FD-DIAB-CODE', recordCode: 'FD', elements: ['DIAB'], severity: 'error', description: 'Diabetes care plan must be Y or N', logic: { kind: 'code_set' }, version: '26.1' },
  ],
  collections: OHIO_FY27.collections.map((c) => ({ ...c, code: c.code.replace('FY27', 'FY26'), name: c.name.replace('FY27', 'FY26'), recordCodes: c.recordCodes.filter((x) => x !== 'CR'), openAt: shiftYear(c.openAt)!, closeAt: shiftYear(c.closeAt)!, correctionOpenAt: shiftYear(c.correctionOpenAt), correctionCloseAt: shiftYear(c.correctionCloseAt), population: c.population.replace(/(\d{4})-/, (_, y: string) => `${Number(y) - 1}-`) })),
  changes: [],
}
